import { ReactElement } from 'react';
import { WeatherHealth, WeatherStatus } from '../types/Run';

interface WeatherSourceBadgeProps {
  weather: WeatherHealth | null | undefined;
  className?: string;
}

const STATUS_STYLES: Record<WeatherStatus, { bg: string; text: string; label: string }> = {
  good: { bg: 'cr-bg-green-100', text: 'cr-text-green-800', label: 'Weather: source ok' },
  fallback: { bg: 'cr-bg-amber-100', text: 'cr-text-amber-800', label: 'Weather: fallback' },
  failed: { bg: 'cr-bg-red-100', text: 'cr-text-red-800', label: 'Weather: sensor only' },
};

const SOURCE_LABEL: Record<string, string> = {
  probe_hybrid: 'Probe-API',
  weather_api: 'Weather API',
  local_station: 'Local Station',
  bike_computer: 'Bike Computer',
  manual: 'Manual',
  probe: 'Probe',
  sensor_fallback: 'On-bike sensor',
};

function labelForSource(source: string | null | undefined): string {
  if (!source) return 'none';
  return SOURCE_LABEL[source] ?? source;
}

export default function WeatherSourceBadge(
  { weather, className }: WeatherSourceBadgeProps,
): ReactElement | null {
  if (!weather) return null;
  const style = STATUS_STYLES[weather.status] ?? STATUS_STYLES.fallback;
  const temp = weather.sources_used.find((u) => u.channel === 'Temp_T');
  const detail = temp
    ? `${labelForSource(temp.actual_source)}${
        temp.fallback_reason ? ` (${temp.fallback_reason.replace(/_/g, ' ')})` : ''
      }`
    : style.label;
  const title = temp && temp.requested_source !== temp.actual_source
    ? `Requested: ${labelForSource(temp.requested_source)} → Using: ${labelForSource(temp.actual_source)}`
    : style.label;
  return (
    <span
      title={title}
      className={`cr-inline-flex cr-items-center cr-gap-1 cr-rounded-full cr-px-2 cr-py-0.5 cr-text-xs cr-font-medium ${style.bg} ${style.text} ${className ?? ''}`}
    >
      <span className="cr-h-1.5 cr-w-1.5 cr-rounded-full cr-bg-current" />
      {detail}
    </span>
  );
}
